'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Globe, ShieldCheck, Radar, Crosshair, Send, Users, Eye, Clock, Activity } from 'lucide-react'
import { baseDashboardClasses } from '../BaseDashboard'
import { PageFrame } from '../ui'
import { apiGet } from './entriesApi'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'

// Brand Integrity: one brand's standing at a glance -- a status orb, three
// numbers and a short narrative, the way a brand owner wants to read it.
// Modelled on the brand-threat-beacon mock (Lovable): the same page for every
// brand, pre-scoped to its platform like Unified Entries, so a tenant only
// ever sees its own standing.
//
// Everything shown is derived server-side (backend queries/brandIntegrity.ts)
// from the brand's unified entries, their domains' browser probes and the
// actions filed for it; the status itself is the backend's rule, repeated in
// the (i) popover. The segmented control shows the computed status; an
// Editor may click another segment to preview how the page reads in that
// state (same numbers, that state's copy) -- the mock's demo toggle, kept
// for showing the product. Nothing on this page writes.

export type IntegrityStatus = 'secure' | 'elevated' | 'under_threat'

type Summary = {
  platform_id: number
  computed_at: string
  status: IntegrityStatus
  rule: { underThreatLiveTargets: number; underThreatLiveAgeHours: number; elevatedRecentDays: number; staleProbeDays: number }
  live: { count: number; oldest_hours: number | null; targets: Array<{ host: string; url: string; detected_at: string | null; last_checked_at: string | null; reports: number }> }
  targets: { total: number; known: number; stale: number; unknown: number; d30: number; d90: number }
  contained: { total: number; d30: number; d90: number; rate_90d: number | null; rate_total: number | null }
  detections: { h72: number; d7: number; d30: number; d90: number; total: number }
  removal: { median_minutes_90d: number | null; median_minutes_total: number | null; timed_90d: number; timed_total: number }
  actions: { filed_total: number; filed_30d: number; filed_quarter: number; acknowledged_total: number; resolved_total: number; pending: number }
  channels: { sources: string[]; count: number }
  watch: { last_probe_at: string | null; last_pipeline_at: string | null; last_entry_at: string | null; entries: number }
}

// Survives brand switches; a refresh clears it.
const _cache = new Map<number, { summary: Summary; at: number }>()
const CACHE_TTL = 5 * 60 * 1000

const STATUS_ORDER: IntegrityStatus[] = ['secure', 'elevated', 'under_threat']
const STATUS_LABEL: Record<IntegrityStatus, string> = { secure: 'Secure', elevated: 'Elevated', under_threat: 'Under threat' }
const STATUS_HEADLINE: Record<IntegrityStatus, string> = {
  secure: 'Protected, and staying that way.',
  elevated: 'Spotted early. Handled fast.',
  under_threat: 'Attacked — and fighting back.',
}
// The orb's colour per status: the theme's own success / warning / destructive
// tokens (globals.css), so the orb follows light and dark like everything else.
const STATUS_COLOR: Record<IntegrityStatus, string> = {
  secure: 'var(--success)',
  elevated: 'var(--warning)',
  under_threat: 'var(--destructive)',
}
const STATUS_TEXT: Record<IntegrityStatus, { light: string; dark: string }> = {
  secure: { light: 'text-success', dark: 'text-success' },
  elevated: { light: 'text-warning', dark: 'text-warning' },
  under_threat: { light: 'text-destructive', dark: 'text-destructive' },
}

// ─── Formatting ───────────────────────────────────────────────────────────────

const HOUR_MIN = 60
const DAY_MIN = 24 * HOUR_MIN

function formatMinutes(minutes: number | null): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—'
  const m = Math.max(0, minutes)
  if (m < HOUR_MIN) return `${Math.round(m)} min`
  if (m < 2 * DAY_MIN) return `${Math.round(m / HOUR_MIN)}h`
  return `${(m / DAY_MIN).toFixed(1)} d`
}

function formatHours(hours: number | null): string {
  if (hours == null || !Number.isFinite(hours)) return '—'
  if (hours < 1) return 'under an hour'
  if (hours < 48) return `${Math.round(hours)} hours`
  return `${Math.round(hours / 24)} days`
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'never'
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return 'unknown'
  const diff = Date.now() - ms
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m} min ago`
  const h = Math.floor(m / 60)
  if (h < 48) return `${h}h ago`
  return `${Math.floor(h / 24)} d ago`
}

function percent(rate: number | null): string {
  return rate == null ? '—' : `${Math.round(rate * 100)}%`
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

// Pages last seen up but not re-checked within the rule's window: never
// claimed as live, never hidden either.
const unverified = (s: Summary) =>
  s.targets.stale > 0 ? ` ${plural(s.targets.stale, 'older page')} last seen up ${s.targets.stale === 1 ? 'has' : 'have'} not been re-checked in ${s.rule.staleProbeDays}+ days.` : ''

// ─── Copy per status, with the real numbers ──────────────────────────────────

type Tile = { label: string; value: string }
type Item = { icon: React.ComponentType<{ className?: string }>; title: string; body: string }

function composeCopy(status: IntegrityStatus, s: Summary, brand: string): { tiles: Tile[]; items: Item[] } {
  const live = s.live.count
  const lastCheck = relativeTime(s.watch.last_probe_at)
  const sourcesList = s.channels.sources.slice(0, 4).join(', ') + (s.channels.count > 4 ? ` and ${s.channels.count - 4} more` : '')
  const median90 = formatMinutes(s.removal.median_minutes_90d)

  if (status === 'secure') {
    return {
      tiles: [
        { label: 'Active threats', value: String(live) },
        { label: 'Pages contained', value: s.targets.known > 0 ? `${s.contained.total}/${s.targets.known}` : '—' },
        { label: 'Last check', value: s.watch.last_probe_at ? relativeTime(s.watch.last_probe_at) : '—' },
      ],
      items: [
        { icon: Globe, title: 'Nothing gets through', body:
          (s.detections.h72 === 0
            ? 'No scam report naming the brand in the last 72 hours. '
            : `${plural(s.detections.h72, 'scam report')} in the last 72 hours, none with a page still up. `)
          + (s.targets.known > 0
            ? `Every one of the ${plural(s.targets.known, 'page')} checked to date is down.`
            : 'No fraudulent page has been reported so far.')
          + unverified(s) },
        { icon: ShieldCheck, title: 'Your channels, verified', body:
          s.channels.count > 0
            ? `${plural(s.channels.count, 'source')} feed this brand's watch (${sourcesList}). ${plural(s.detections.total, 'report')} screened in all, ${s.watch.entries} entries reviewed.`
            : 'No source has reported anything for this brand yet; the watch is in place.' },
        { icon: Radar, title: 'Watch never pauses', body:
          `Reported pages stay on the monitor's rolling re-probe; the last check ran ${lastCheck}. The moment anything comes back up, your team knows first.` },
      ],
    }
  }

  if (status === 'elevated') {
    return {
      tiles: [
        { label: 'Lookalikes contained', value: String(s.contained.d30) },
        { label: 'Median response', value: median90 },
        { label: 'Takedowns this quarter', value: String(s.actions.filed_quarter) },
      ],
      items: [
        { icon: Crosshair, title: 'Caught early', body:
          `${plural(s.detections.d7, 'scam report')} in the last 7 days`
          + (live > 0 ? `, ${plural(live, 'page')} still up and under watch` : ', every reported page already down')
          + (s.live.oldest_hours != null && live > 0 ? ` — the oldest for ${formatHours(s.live.oldest_hours)}.` : '.') },
        { icon: Send, title: 'Contained at the source', body:
          `${s.contained.d30} of ${plural(s.targets.d30, 'page')} reported in the last 30 days are down. `
          + `${plural(s.actions.filed_30d, 'takedown request')} went out in the same period${s.actions.pending > 0 ? `, ${s.actions.pending} more planned` : ''}.` },
        { icon: Activity, title: 'Your record holds', body:
          (s.removal.timed_90d > 0
            ? `Median detection-to-removal of ${median90} over the last 90 days (${plural(s.removal.timed_90d, 'page')} timed). `
            : 'No page needed a timed removal in the last 90 days. ')
          + (live > 0 ? 'Review the live targets below and confirm any authorised partner.' : `The last check ran ${lastCheck}.`)
          + unverified(s) },
      ],
    }
  }

  return {
    tiles: [
      { label: 'Live threats', value: String(live) },
      { label: 'Median time to removal', value: median90 },
      { label: 'Enforcement success', value: percent(s.contained.rate_90d) },
    ],
    items: [
      { icon: Crosshair, title: 'The campaign is mapped', body:
        `${plural(live, 'page')} impersonating ${brand} ${live === 1 ? 'is' : 'are'} up right now`
        + (s.live.oldest_hours != null ? `, the oldest for ${formatHours(s.live.oldest_hours)}` : '')
        + `. ${plural(s.targets.d30, 'target')} reported in the last 30 days, ${plural(s.detections.d30, 'report')} screened.`
        + unverified(s) },
      { icon: Send, title: 'Takedowns in motion', body:
        `${plural(s.actions.filed_30d, 'removal request')} filed in the last 30 days`
        + (s.actions.pending > 0 ? `, ${s.actions.pending} planned` : '')
        + `; ${plural(s.contained.d30, 'page')} reported this month ${s.contained.d30 === 1 ? 'is' : 'are'} already offline`
        + (s.contained.rate_90d != null ? ` (${percent(s.contained.rate_90d)} of the last 90 days' targets)` : '') + '.' },
      { icon: Users, title: 'Customers kept safe', body:
        `Every live target stays on the monitor's rolling re-probe; the last check ran ${lastCheck}. `
        + `${plural(s.channels.count, 'source')} keep reporting${s.channels.count > 0 ? ` (${sourcesList})` : ''}, so a new page is on this board as soon as one of them sees it.` },
    ],
  }
}

// ─── The orb (ported from the mock's CSS; --c is the status colour) ──────────

const ORB_CSS = `
.bi-stage { position: relative; display: grid; place-items: center; }
.bi-stage::after {
  content: ""; position: absolute; left: 50%; bottom: -9%; translate: -50%;
  width: 58%; height: 12%; border-radius: 9999px; filter: blur(11px);
  background: radial-gradient(ellipse at center, color-mix(in oklab, var(--c) 60%, transparent) 0%, transparent 70%);
}
.bi-orb {
  position: relative; border-radius: 9999px;
  background:
    radial-gradient(circle at 30% 18%, oklch(100% 0 0) 0%, oklch(100% 0 0 / .95) 1.5%, oklch(100% 0 0 / 0) 4.5%),
    radial-gradient(circle at 22% 30%, oklch(100% 0 0 / .9) 0%, oklch(100% 0 0 / 0) 2.5%),
    radial-gradient(circle at 78% 82%, oklch(100% 0 0 / .9) 0%, oklch(100% 0 0 / 0) 3%),
    radial-gradient(ellipse 40% 15% at 50% 91%, color-mix(in oklab, var(--c) 75%, white 25%) 0%, transparent 66%),
    radial-gradient(circle at 50% 50%, transparent 73%, color-mix(in oklab, var(--c) 62%, transparent) 90%, color-mix(in oklab, var(--c) 26%, transparent) 100%),
    linear-gradient(177deg, var(--g1) 0%, var(--g2) 48%, var(--g1) 100%),
    radial-gradient(circle at 50% 46%, var(--g3) 0%, var(--g4) 65%, var(--g5) 100%);
  box-shadow:
    0 0 24px 1px color-mix(in oklab, var(--c) 22%, transparent),
    0 32px 42px -30px oklch(30% .03 260 / .4),
    inset 0 0 0 1px oklch(100% 0 0 / .8),
    inset 0 10px 20px oklch(100% 0 0 / .6),
    inset 0 -12px 24px color-mix(in oklab, var(--c) 28%, transparent);
  transition: box-shadow .6s;
  animation: bi-breathe 5s ease-in-out infinite;
}
.bi-orb::before {
  content: ""; position: absolute; inset: 0; border-radius: 9999px; pointer-events: none;
  box-shadow: inset 0 0 9px 3px color-mix(in oklab, var(--c) 60%, oklch(30% .05 260 / .85)), inset 0 0 2px oklch(28% .04 260 / .45);
}
.bi-orb::after {
  content: ""; position: absolute; inset: -12%; border-radius: 9999px;
  border: 1px solid color-mix(in oklab, var(--c) 20%, transparent);
  animation: bi-ripple 4.5s ease-out infinite;
}
.bi-orb.bi-danger { animation-duration: 2.2s; }
.bi-orb.bi-danger::after { animation-duration: 2.2s; }
.bi-light { --g1: oklch(90% .02 250 / .45); --g2: oklch(99% .005 95 / .2); --g3: oklch(99% .005 95 / .35); --g4: oklch(96% .01 240 / .3); --g5: oklch(92% .015 240 / .4); }
.bi-dark  { --g1: oklch(40% .03 250 / .55); --g2: oklch(60% .01 95 / .25); --g3: oklch(70% .01 95 / .35); --g4: oklch(45% .02 240 / .4); --g5: oklch(30% .02 240 / .5); }
@keyframes bi-breathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.03); } }
@keyframes bi-ripple { 0% { opacity: .6; transform: scale(.85); } 100% { opacity: 0; transform: scale(1.35); } }
@media (prefers-reduced-motion: reduce) { .bi-orb, .bi-orb::after { animation: none; } }
`

// ─── View ─────────────────────────────────────────────────────────────────────

export function BrandIntegrityView({ theme, platformId, brandLabel, brandId, isEditor }: {
  theme: ProcessedTheme
  platformId: number
  brandLabel: string
  brandId: string
  isEditor?: boolean
}) {
  const { isDark, border, muted, label, text } = baseDashboardClasses(theme)

  const cached = _cache.get(platformId)
  const fresh = cached != null && Date.now() - cached.at < CACHE_TTL
  const [summary, setSummary] = useState<Summary | null>(fresh ? cached.summary : null)
  const [lastUpdated, setLastUpdated] = useState<number | null>(fresh ? cached.at : null)
  const [loading, setLoading] = useState(!fresh)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // An Editor's preview of another status; null = the computed one.
  const [preview, setPreview] = useState<IntegrityStatus | null>(null)

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true)
    setError(null)
    const result = await apiGet<Summary>(`/brand-integrity?platform_id=${platformId}`)
    if (result.success && result.data) {
      const at = Date.now()
      _cache.set(platformId, { summary: result.data, at })
      setSummary(result.data)
      setLastUpdated(at)
    } else {
      setError(result.error ?? 'Failed to load')
    }
    setLoading(false)
    setRefreshing(false)
  }, [platformId])

  useEffect(() => {
    setPreview(null)
    const c = _cache.get(platformId)
    if (c && Date.now() - c.at < CACHE_TTL) {
      setSummary(c.summary); setLastUpdated(c.at); setLoading(false)
      return
    }
    setSummary(null); setLoading(true)
    void load()
  }, [platformId, load])

  const computed = summary?.status ?? null
  const status: IntegrityStatus = preview ?? computed ?? 'secure'
  const copy = useMemo(() => (summary ? composeCopy(status, summary, brandLabel) : null), [status, summary, brandLabel])
  const tone = isDark ? STATUS_TEXT[status].dark : STATUS_TEXT[status].light
  const heading = 'text-foreground'
  const body = 'text-muted-foreground'
  const hairline = 'border-border'

  const info = summary ? (
    <div className="space-y-1.5 text-[12px]">
      <p>Computed on read from this brand&apos;s unified entries (platform id {summary.platform_id}), their domains&apos; browser probes and the actions filed for it. A <b>threat</b> is an entry the classifier called a scam that no moderator rejected; a <b>live</b> page is one whose latest probe saw it up and nobody marked inactive.</p>
      <p><b>Under threat</b>: at least {summary.rule.underThreatLiveTargets} live pages, or one live for {summary.rule.underThreatLiveAgeHours}h+ since detection. <b>Elevated</b>: any live page, or a scam report in the last {summary.rule.elevatedRecentDays} days. Otherwise <b>secure</b>.</p>
      <p>A page last seen up but not re-probed for {summary.rule.staleProbeDays}+ days is unverified ({summary.targets.stale}), and one never probed ({summary.targets.unknown}) is unknown: neither counts as live or contained.</p>
    </div>
  ) : undefined

  const statusControl = (
          <div className="flex items-center gap-2">
            {preview && preview !== computed && (
              <span className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>preview · computed is {computed ? STATUS_LABEL[computed] : '—'}</span>
            )}
            <div className={`flex gap-1 rounded border p-1 ${border} bg-card`} role="radiogroup" aria-label="Status">
              {STATUS_ORDER.map((st) => {
                // Nothing is highlighted until the status is known.
                const active = !!summary && st === status
                const canPreview = !!isEditor && !!summary
                return (
                  <button
                    key={st}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={!canPreview}
                    title={canPreview ? (st === computed ? 'Computed status' : `Preview the page as ${STATUS_LABEL[st]}`) : undefined}
                    onClick={() => setPreview(st === computed ? null : st)}
                    className={`relative rounded px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-widest transition ${
                      active
                        ? ('bg-primary text-primary-foreground')
                        : `${muted} ${canPreview ? 'hover:text-signal' : 'cursor-default'}`
                    }`}
                  >
                    {STATUS_LABEL[st]}
                    {st === computed && !active && <span className="absolute right-1 top-1 h-1 w-1 rounded-full bg-signal" aria-hidden />}
                  </button>
                )
              })}
            </div>
          </div>
  )

  return (
    <PageFrame
      eyebrow={`${brandLabel} · Moderation`}
      title="Brand integrity"
      info={info}
      refresh={{ onRefresh: () => { _cache.delete(platformId); void load(true) }, refreshing, lastUpdated, storageKey: `${brandId}-integrity` }}
      headerActions={statusControl}
    >
      <style>{ORB_CSS}</style>
      <div className="mx-auto w-full max-w-5xl px-4 pb-16 pt-6 md:px-8">

        {loading && (
          <div className="flex h-64 items-center justify-center">
            <span className={`animate-pulse font-mono text-[11px] uppercase tracking-widest ${muted}`}>Computing…</span>
          </div>
        )}
        {error && !loading && (
          <div className="flex h-64 flex-col items-center justify-center gap-3">
            <span className="font-mono text-[13px] text-destructive">{error}</span>
            <button onClick={() => void load(true)} className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:border-signal hover:text-signal ${border} ${muted}`}>Retry</button>
          </div>
        )}

        {summary && copy && !loading && (
          <>
            {/* ── Three numbers ─────────────────────────────────────────── */}
            <div className={`mt-8 grid grid-cols-3 divide-x border-y ${hairline} divide-border`}>
              {copy.tiles.map((t) => (
                <div key={t.label} className="px-5 py-5 first:pl-0">
                  <p className={`font-mono text-[9px] uppercase tracking-[0.2em] ${muted}`}>{t.label}</p>
                  <p className={`mt-1 font-mono text-3xl tabular-nums ${tone}`}>{t.value}</p>
                </div>
              ))}
            </div>

            {/* ── The orb ───────────────────────────────────────────────── */}
            <div className="mt-10 flex flex-col items-center">
              <div className={`bi-stage bi-light`} style={{ ['--c' as string]: STATUS_COLOR[status] }}>
                <div className={`bi-orb h-48 w-48 md:h-60 md:w-60 ${status === 'under_threat' ? 'bi-danger' : ''}`} aria-label={`Status: ${STATUS_LABEL[status]}`} />
              </div>
              <p className={`mt-10 font-mono text-[10px] uppercase tracking-[0.3em] ${tone}`}>{STATUS_LABEL[status]}</p>
              <h2 className={`mt-2 text-center text-2xl font-semibold tracking-tight md:text-3xl ${heading}`}>{STATUS_HEADLINE[status]}</h2>
            </div>

            {/* ── The story ─────────────────────────────────────────────── */}
            <div className={`mt-12 border-t ${hairline}`}>
              {copy.items.map((item, i) => {
                const Icon = item.icon
                return (
                  <div key={item.title} className={`grid grid-cols-[2.5rem_1fr] gap-x-2 py-6 ${i > 0 ? `border-t ${hairline}` : ''}`}>
                    <span className={`pt-1 font-mono text-[10px] ${muted}`}>{String(i + 1).padStart(2, '0')}</span>
                    <div>
                      <div className={`flex items-center gap-2 text-[15px] font-semibold ${text}`}>
                        <Icon className={`h-4 w-4 ${label}`} />
                        {item.title}
                      </div>
                      <p className={`mt-1.5 max-w-2xl text-[13px] leading-relaxed ${body}`}>{item.body}</p>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* ── Live targets, when there are any ──────────────────────── */}
            {summary.live.count > 0 && (
              <div className={`border-t pt-6 ${hairline}`}>
                <p className={`font-mono text-[9px] uppercase tracking-[0.2em] ${muted}`}>Live targets · {summary.live.count}</p>
                <ul className={`mt-3 divide-y divide-border`}>
                  {summary.live.targets.map((t) => (
                    <li key={t.host} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5">
                      <span className={`truncate font-mono text-[13px] ${text}`} title={t.url}>{t.host}</span>
                      <span className={`flex items-center gap-4 font-mono text-[10px] uppercase tracking-widest ${muted}`}>
                        <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{plural(t.reports, 'report')}</span>
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />seen {relativeTime(t.detected_at)}</span>
                        <span>checked {relativeTime(t.last_checked_at)}</span>
                      </span>
                    </li>
                  ))}
                  {summary.live.count > summary.live.targets.length && (
                    <li className={`py-2 font-mono text-[10px] uppercase tracking-widest ${muted}`}>and {summary.live.count - summary.live.targets.length} more</li>
                  )}
                </ul>
              </div>
            )}

            <p className={`mt-10 text-center font-mono text-[9px] uppercase tracking-[0.25em] ${muted}`}>
              Continuously monitored across {plural(summary.channels.count, 'channel')} · computed {relativeTime(summary.computed_at)}
            </p>
          </>
        )}
      </div>
    </PageFrame>
  )
}
